<script setup lang="ts">
/**
 * Общее — макет 84:575: название, ключ, описание и блок доступа.
 *
 * Ключ показан и заблокирован — так же считает и сервис: менять его нельзя,
 * поля `key` нет даже в теле `UpdateProject`. На нём висят номера задач и
 * привязка веток GitLab.
 *
 * Из четырёх переключателей макета здесь один — приватность. «Переносить
 * задачи может любой участник», «Требовать оценку перед переводом в работу» и
 * «Разрешить DevOps-действия из карточки» в модели проекта не существуют, а
 * тумблер, который не переживает перезагрузку, врёт убедительнее отсутствия.
 * Всё это — в docs/design-gaps.md.
 *
 * Архивирование стоит отдельной кнопкой, а не в общем черновике настроек: оно
 * уводит проект из хаба сразу, и «сохранить» ему не нужно.
 */
import { AppButton, AppModal, AppSwitch, AppTextField, useToast } from '@vinprop/design-system'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import { useProjectsStore } from '@/features/projects/store'

import { useSettingsStore } from '../store'

const { t } = useI18n()
const store = useSettingsStore()
const projects = useProjectsStore()
const toast = useToast()

const isAsking = ref(false)
const busy = ref(false)

const isArchived = computed(() =>
  Boolean(projects.items.find((project) => project.id === store.project?.id)?.archivedAt),
)

const toggleArchived = async () => {
  if (!store.project) return

  busy.value = true

  try {
    await projects.setArchived(store.project.id, !isArchived.value)
    isAsking.value = false
    toast.success(t(isArchived.value ? 'settings.general.restored' : 'settings.general.archived'))
  } catch (failure) {
    toast.error(errorMessage(failure, t('settings.saveFailed')))
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="gtab">
    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.name') }}</span>
      <AppTextField v-model="store.form.name" :aria-label="t('settings.general.name')" />
    </label>

    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.key') }}</span>
      <AppTextField
        :model-value="store.project?.key ?? ''"
        disabled
        :aria-label="t('settings.general.key')"
      />
      <span class="gtab__note">{{ t('settings.general.keyNote') }}</span>
    </label>

    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.description') }}</span>
      <AppTextField
        v-model="store.form.description"
        :aria-label="t('settings.general.description')"
      />
    </label>

    <p class="gtab__overline">{{ t('settings.general.access') }}</p>

    <div class="gtab__card">
      <div class="grow">
        <div class="grow__text">
          <span class="grow__name">{{ t('settings.general.private') }}</span>
          <span class="grow__note">{{ t('settings.general.privateNote') }}</span>
        </div>

        <span class="grow__spacer" />

        <AppSwitch v-model="store.form.isPrivate" :aria-label="t('settings.general.private')" />
      </div>

      <!--
        Ассистент включается по проектам, а не за всю компанию (ADR-0048,
        DEV-1567): содержимое задач уходит во внешнюю модель, и в проекте со
        сделками там оказались бы имена и телефоны клиентов. Выключено — это
        нормальное состояние проекта, а не поломка.
      -->
      <div class="grow">
        <div class="grow__text">
          <span class="grow__name">{{ t('settings.general.ai') }}</span>
          <span class="grow__note">{{ t('settings.general.aiNote') }}</span>
        </div>

        <span class="grow__spacer" />

        <AppSwitch v-model="store.form.aiEnabled" :aria-label="t('settings.general.ai')" />
      </div>
    </div>

    <p class="gtab__overline">{{ t('settings.general.archive') }}</p>

    <div class="gtab__card">
      <div class="grow">
        <div class="grow__text">
          <span class="grow__name">
            {{
              isArchived ? t('settings.general.restoreTitle') : t('settings.general.archiveTitle')
            }}
          </span>
          <span class="grow__note">{{ t('settings.general.archiveNote') }}</span>
        </div>

        <span class="grow__spacer" />

        <AppButton variant="secondary" size="small" @click="isAsking = true">
          {{
            isArchived ? t('settings.general.restoreAction') : t('settings.general.archiveAction')
          }}
        </AppButton>
      </div>
    </div>
  </div>

  <AppModal
    v-if="isAsking"
    model-value
    :title="isArchived ? t('settings.general.restoreTitle') : t('settings.general.archiveTitle')"
    size="small"
    @update:model-value="isAsking = false"
  >
    <p class="gtab__ask">
      {{ isArchived ? t('settings.general.restoreAsk') : t('settings.general.archiveAsk') }}
    </p>

    <template #footer>
      <AppButton variant="secondary" @click="isAsking = false">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" @click="toggleArchived">
        {{ isArchived ? t('settings.general.restoreAction') : t('settings.general.archiveAction') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.gtab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gtab__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 520px;
  max-width: 100%;
}

.gtab__label,
.gtab__ask {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}

.gtab__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.gtab__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.gtab__card {
  display: flex;
  flex-direction: column;
  margin-top: -12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.grow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.grow:first-child {
  border-top: none;
}

.grow__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grow__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.grow__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.grow__spacer {
  flex: 1;
}
</style>
